import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const CartContext = createContext(null);

const keyOf = (slug, color, size) => `${slug}|${color || ""}|${size || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("sp_cart") || "[]");
      return raw.map((i) => ({ ...i, key: i.key || keyOf(i.slug, i.color, i.size) }));
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem("sp_cart", JSON.stringify(items)); }, [items]);

  const add = useCallback((product, qty = 1, variant = null) => {
    const size = variant?.size || null;
    const color = variant?.color || null;
    const key = keyOf(product.slug, color, size);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, { key, slug: product.slug, name: product.name, price_cents: product.price_cents, image: variant?.image || product.image, quantity: qty, size, color, printful_variant_id: variant?.printful_variant_id || null }];
    });
  }, []);

  const remove = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const setQty = useCallback((key, qty) => setItems((prev) => prev.map((i) => i.key === key ? { ...i, quantity: Math.max(1, qty) } : i)), []);
  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const totalCents = useMemo(() => items.reduce((s, i) => s + i.price_cents * i.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, count, totalCents, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
